import { useCallback, useEffect, useState } from 'react';

import { PlusIcon, TriangleAlertIcon } from 'lucide-react';
import { Controller, useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Accordion } from '@/components/ui/accordion';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { WebWidgetConfig } from '@/shared/types/integration';

import AddWebpageCard from './AddWebpageCard';
import TeaserFields from './TeaserFields';
import TeaserPageCard from './TeaserPageCard';
import { TeaserHostForm, TeaserPageForm } from './types';
import { useSuggestedWebsiteUrl } from './useSuggestedWebsiteUrl';

type Props = {
  readOnly?: boolean;
  deskId?: string;
  integrationId?: string;
  /** Whether this integration also has `page_triggers`, whose copy outranks ours. */
  hasPageTriggers?: boolean;
  /** The saved config, for suggesting the site a new page hangs off. */
  config?: WebWidgetConfig;
};

/**
 * The `teaser` block: what the visitor sees beside the closed launcher. It
 * stands where the launcher's old "Teaser message" field was.
 */
const Teaser = ({ readOnly, hasPageTriggers, deskId, integrationId, config }: Props) => {
  const { t } = useTranslation();
  const { control, getValues, setValue } = useFormContext<TeaserHostForm>();

  const perPage = useWatch({ control, name: 'teaser.per_page' });
  const websiteUrl = useWatch({ control, name: 'teaser.website_url' }) ?? '';

  const { fields, append, remove } = useFieldArray({ control, name: 'teaser.pages' });

  const [isAdding, setIsAdding] = useState(false);
  /**
   * Which rows are open. Held here rather than by the accordion so a row that
   * has just been generated can be opened for the author, which is the only
   * moment anything but a click should move it.
   */
  const [openRows, setOpenRows] = useState<string[]>([]);

  const suggestedWebsiteUrl = useSuggestedWebsiteUrl(config);

  /**
   * Turning the switch on with nothing to show is a dead end, so open the add
   * card with it. Turning it off closes the card — the rows themselves stay
   * resident, and the serialiser is what drops them.
   */
  useEffect(() => {
    if (!perPage) {
      setIsAdding(false);
    }
  }, [perPage]);

  const handleAdd = useCallback(
    (origin: string, page: TeaserPageForm) => {
      /*
        The site, recorded once from the first address anyone typed.

        `shouldDirty: false` is wrong here and right in the old prefill: this
        *is* an edit — the author added a page — and the row appended below
        dirties the form anyway.
      */
      if (!(getValues('teaser.website_url') ?? '').trim()) {
        setValue('teaser.website_url', origin, { shouldDirty: true });
      }
      // Append, never prepend: `pages[]` order is the matching semantics, so
      // inserting at the front would change which teaser an existing URL
      // already resolves to.
      append(page);
      setIsAdding(false);
      // Open what was just made, so the actions are there to read rather than
      // behind a click on a row that appeared from nowhere.
      setOpenRows((open) => [...open, `page-${fields.length}`]);
    },
    [append, fields.length, getValues, setValue]
  );

  const handleRemove = useCallback(
    (index: number) => {
      remove(index);
      /*
        Row values are positional, so every row after this one shifts down and
        the open set would now name the wrong rows. Closing them all is honest;
        remapping would be guessing which one the author still wanted open.
      */
      setOpenRows([]);
    },
    [remove]
  );

  return (
    <div className="flex flex-col gap-4">
      {/*
        No heading of its own. The variant select is labelled "Teaser" and
        carries the help line underneath, so a title above it said the same
        word twice.
      */}
      {hasPageTriggers && (
        <Alert variant="warning">
          <TriangleAlertIcon />
          <AlertDescription>{t('integrations.web_teaser.page_triggers_warning')}</AlertDescription>
        </Alert>
      )}

      <TeaserFields prefix="teaser" readOnly={readOnly} />

      <Separator />

      <div className="flex items-start gap-4">
        <div className="flex flex-1 flex-col gap-1">
          <span className="text-sm leading-none font-medium">
            {t('integrations.web_teaser.per_page.title')}
          </span>
          <span className="text-sm leading-5 text-foreground-muted">
            {t('integrations.web_teaser.per_page.subtitle')}
          </span>
        </div>
        <Controller
          control={control}
          name="teaser.per_page"
          render={({ field }) => (
            <Switch
              checked={!!field.value}
              disabled={readOnly}
              aria-label={t('integrations.web_teaser.per_page.title')}
              onCheckedChange={field.onChange}
            />
          )}
        />
      </div>

      {perPage && (
        <div className="flex flex-col gap-4">
          {fields.length > 0 && (
            <Accordion type="multiple" value={openRows} onValueChange={setOpenRows}>
              <div className="flex flex-col gap-4">
                {fields.map((field, index) => (
                  <TeaserPageCard
                    key={field.id}
                    index={index}
                    value={`page-${index}`}
                    readOnly={readOnly}
                    onRemove={() => handleRemove(index)}
                    deskId={deskId}
                    integrationId={integrationId}
                  />
                ))}
              </div>
            </Accordion>
          )}

          {isAdding && (
            <AddWebpageCard
              websiteUrl={websiteUrl}
              suggestedHost={suggestedWebsiteUrl}
              deskId={deskId}
              integrationId={integrationId}
              onAdd={handleAdd}
              onCancel={() => setIsAdding(false)}
            />
          )}

          {!readOnly && !isAdding && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="self-start"
              onClick={() => setIsAdding(true)}
            >
              <PlusIcon size={16} />
              {t('integrations.web_teaser.pages.add_webpage')}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default Teaser;
